import React, { useState } from 'react';
import {
  BookOpen, Home, Gamepad2, Shield, User, Bell, Award,
  HelpCircle, CheckCircle, ArrowRight, AlertTriangle,
  Info, AlertCircle, Star, TrendingUp, MessageSquare,
  ChevronRight, Heart, Clock, Zap, Lock, Eye,
} from 'lucide-react';

interface DocumentationProps {
  darkMode?: boolean;
}

// ── Shared primitives ─────────────────────────────────────────────────────────

function Badge({ children, variant = 'blue', dm = false }: { children: React.ReactNode; variant?: 'blue' | 'green' | 'red' | 'yellow' | 'slate'; dm?: boolean }) {
  const colors: Record<string, { light: string; dark: string }> = {
    blue:   { light: 'bg-blue-100 text-blue-700 border-blue-200',        dark: 'bg-blue-500/15 text-blue-300 border-blue-500/30'       },
    green:  { light: 'bg-emerald-100 text-emerald-700 border-emerald-200', dark: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' },
    red:    { light: 'bg-red-100 text-red-700 border-red-200',            dark: 'bg-rose-500/15 text-rose-300 border-rose-500/30'       },
    yellow: { light: 'bg-amber-100 text-amber-700 border-amber-200',      dark: 'bg-amber-500/15 text-amber-300 border-amber-500/30'    },
    slate:  { light: 'bg-slate-100 text-slate-600 border-slate-200',      dark: 'bg-slate-500/20 text-slate-300 border-slate-500/30'    },
  };
  const c = colors[variant];
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border ${dm ? c.dark : c.light}`}>
      {children}
    </span>
  );
}

function Callout({ type = 'info', title, children, dm = false }: { type?: 'info' | 'warning' | 'tip'; title: string; children: React.ReactNode; dm?: boolean }) {
  const styles: Record<string, { light: string; dark: string; iconColor: string; icon: (cls: string) => React.ReactNode }> = {
    info: {
      light: 'bg-blue-50 border-blue-300 text-blue-900',
      dark:  'bg-blue-500/10 border-blue-500/30 text-blue-200',
      iconColor: 'text-blue-500',
      icon: (cls) => <Info size={16} className={`${cls} shrink-0 mt-0.5`} />,
    },
    warning: {
      light: 'bg-amber-50 border-amber-300 text-amber-900',
      dark:  'bg-amber-500/10 border-amber-500/30 text-amber-200',
      iconColor: 'text-amber-500',
      icon: (cls) => <AlertCircle size={16} className={`${cls} shrink-0 mt-0.5`} />,
    },
    tip: {
      light: 'bg-emerald-50 border-emerald-300 text-emerald-900',
      dark:  'bg-emerald-500/10 border-emerald-500/30 text-emerald-200',
      iconColor: 'text-emerald-500',
      icon: (cls) => <CheckCircle size={16} className={`${cls} shrink-0 mt-0.5`} />,
    },
  };
  const s = styles[type];
  return (
    <div className={`flex gap-3 p-4 rounded-xl border my-6 ${dm ? s.dark : s.light}`}>
      {s.icon(s.iconColor)}
      <div>
        <p className="font-semibold text-sm mb-1">{title}</p>
        <p className="text-sm opacity-90 leading-relaxed">{children}</p>
      </div>
    </div>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xl font-bold text-slate-900 mt-10 mb-4 pb-2 border-b border-slate-200 flex items-center gap-2">
      {children}
    </h2>
  );
}

function SectionHeadingDark({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="text-xl font-bold text-slate-100 mt-10 mb-4 pb-2 border-b border-slate-700 flex items-center gap-2">
      {children}
    </h2>
  );
}

function StepList({ steps }: { steps: { title: string; desc: string }[] }) {
  return (
    <ol className="space-y-4 my-6">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
            {i + 1}
          </span>
          <div>
            <p className="font-semibold text-slate-800 text-sm">{step.title}</p>
            <p className="text-slate-500 text-sm mt-0.5 leading-relaxed">{step.desc}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function StepListDark({ steps }: { steps: { title: string; desc: string }[] }) {
  return (
    <ol className="space-y-4 my-6">
      {steps.map((step, i) => (
        <li key={i} className="flex gap-4 items-start">
          <span className="flex-shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center mt-0.5">
            {i + 1}
          </span>
          <div>
            <p className="font-semibold text-slate-200 text-sm">{step.title}</p>
            <p className="text-slate-400 text-sm mt-0.5 leading-relaxed">{step.desc}</p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function FeatureGrid({ features, dm }: { features: { icon: React.ReactNode; title: string; desc: string }[]; dm: boolean }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 my-6">
      {features.map((f, i) => (
        <div key={i} className={`rounded-xl p-4 border ${dm ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-8 h-8 rounded-lg bg-blue-600/10 flex items-center justify-center text-blue-600">
              {f.icon}
            </div>
            <p className={`font-semibold text-sm ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{f.title}</p>
          </div>
          <p className={`text-sm leading-relaxed ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{f.desc}</p>
        </div>
      ))}
    </div>
  );
}

// ── Section content renderers ─────────────────────────────────────────────────

function OverviewSection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  const cards = [
    { icon: <Home size={18} />,          title: 'Home Dashboard',   desc: 'Your points, wellness score, streaks, and daily tips.' },
    { icon: <BookOpen size={18} />,      title: 'Classroom',        desc: 'Digital activity log, classroom events, and safety alerts.' },
    { icon: <Gamepad2 size={18} />,      title: 'Games',            desc: 'Earn points playing digital-citizenship games.' },
    { icon: <Shield size={18} />,        title: 'Safety & Support', desc: 'Crisis resources, counselling contacts, and online safety tips.' },
    { icon: <User size={18} />,          title: 'Profile',          desc: 'Your account details, badges, and app settings.' },
    { icon: <Bell size={18} />,          title: 'Notifications',    desc: 'Messages from your teacher and safety system alerts.' },
    { icon: <MessageSquare size={18} />, title: 'Self-Correction',  desc: 'Understand and use the rephrase tool when it appears.' },
    { icon: <HelpCircle size={18} />,    title: 'Getting Started',  desc: 'New here? Follow the quick-start guide.' },
  ];
  return (
    <div>
      <div className={`rounded-2xl p-6 mb-8 ${dm ? 'bg-gradient-to-br from-slate-800 to-slate-900 border border-slate-700' : 'bg-gradient-to-br from-indigo-600 to-blue-700'}`}>
        <div className="flex items-center gap-3 mb-3">
          <BookOpen size={28} className="text-white" />
          <h1 className="text-2xl font-bold text-white">Student Guide</h1>
        </div>
        <p className="text-blue-100 text-sm leading-relaxed max-w-xl">
          Everything you need to understand ShieldED Haven — from earning points to getting help fast.
          Use the sidebar to jump to any section.
        </p>
      </div>

      <H2>What's in this guide</H2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {cards.map((c, i) => (
          <div key={i} className={`rounded-xl p-4 border flex items-start gap-3 transition-all ${dm ? 'bg-slate-800 border-slate-700 hover:border-blue-500' : 'bg-white border-slate-200 hover:border-blue-400 hover:shadow-sm'}`}>
            <div className="w-9 h-9 rounded-lg bg-blue-600/10 flex items-center justify-center text-blue-600 shrink-0">
              {c.icon}
            </div>
            <div className="min-w-0">
              <p className={`font-semibold text-sm ${dm ? 'text-slate-200' : 'text-slate-800'}`}>{c.title}</p>
              <p className={`text-xs leading-relaxed ${dm ? 'text-slate-400' : 'text-slate-500'}`}>{c.desc}</p>
            </div>
          </div>
        ))}
      </div>

      <Callout type="tip" title="Quick tip" dm={dm}>
        If you ever need immediate help, tap the <strong>Safety</strong> tab — crisis support
        is available 24/7, no questions asked.
      </Callout>
    </div>
  );
}

function HomeSection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="blue" dm={dm}>Dashboard</Badge>
      </div>
      <h1 className={`text-2xl font-bold mb-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Home Dashboard</h1>
      <p className={`text-sm leading-relaxed mb-8 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        The Home tab is the first thing you see when you open the app. It shows your safety score,
        points, class rank, weekly progress, and a rotating digital-citizenship tip.
      </p>

      <H2>What each card means</H2>
      <FeatureGrid dm={dm} features={[
        { icon: <Heart size={16} />,      title: 'Wellness Score',  desc: 'A traffic-light indicator (green / amber / red) based on your recent online behaviour.' },
        { icon: <Star size={16} />,       title: 'Points',          desc: 'Total safety points earned through good digital choices, games, and self-corrections.' },
        { icon: <Award size={16} />,      title: 'Class Rank',      desc: 'Where you sit on your class leaderboard right now.' },
        { icon: <TrendingUp size={16} />, title: 'Weekly Progress', desc: 'A bar chart of your daily points over the last 7 days.' },
        { icon: <Zap size={16} />,        title: 'Level & XP',      desc: 'Every 250 points unlocks a new level name. Watch the XP bar fill up.' },
        { icon: <Clock size={16} />,      title: 'Streak 🔥',       desc: 'Consecutive days with at least one point. Keep it going!' },
      ]} />

      <H2>Wellness traffic light</H2>
      <div className="space-y-2 my-4">
        {[
          { dot: 'bg-emerald-400', label: 'Great', score: '7 – 10', note: 'Your browsing habits are excellent — keep it up!',  color: 'bg-emerald-100 border-emerald-300 text-emerald-800' },
          { dot: 'bg-amber-400',   label: 'Good',  score: '4 – 6',  note: "A few things to watch, but you're on the right track.", color: 'bg-amber-100 border-amber-300 text-amber-800' },
          { dot: 'bg-rose-500',    label: 'Watch', score: '0 – 3',  note: "Let's work on better online habits together.",       color: 'bg-red-100 border-red-300 text-red-800' },
        ].map(r => (
          <div key={r.label} className={`flex items-center gap-4 p-3 rounded-lg border text-sm ${r.color}`}>
            <div className={`w-2.5 h-2.5 rounded-full ${r.dot} shrink-0`} />
            <span className="font-semibold w-20">{r.label}</span>
            <span className="font-mono text-xs opacity-70">{r.score}</span>
            <span className="text-xs">{r.note}</span>
          </div>
        ))}
      </div>

      <Callout type="info" title="Parent transparency" dm={dm}>
        If you have flagged events, a notice appears on your Home screen letting you know your parent
        or guardian has been informed. Staying safe keeps that notice away.
      </Callout>
    </div>
  );
}

function ClassroomSection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="blue" dm={dm}>Activity Log</Badge>
      </div>
      <h1 className={`text-2xl font-bold mb-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Classroom</h1>
      <p className={`text-sm leading-relaxed mb-8 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        The Classroom tab shows everything the system has recorded about your digital activity —
        split into three tabs: Digital, Classroom, and Alerts.
      </p>

      <H2>The three tabs explained</H2>
      <FeatureGrid dm={dm} features={[
        { icon: <Eye size={16} />,           title: 'Digital',   desc: 'Meaningful browser events — self-corrections, warnings, and points-earning moments. Routine safe browsing is hidden by default.' },
        { icon: <BookOpen size={16} />,      title: 'Classroom', desc: 'Activities recorded by your teacher — participation, assignments, and classroom conduct.' },
        { icon: <AlertTriangle size={16} />, title: 'Alerts',    desc: "Content flagged by the safety system. This isn't punishment — it's here to help you make better choices." },
      ]} />

      <H2>Summary stats at the top</H2>
      <div className={`rounded-xl border p-4 mb-6 font-mono text-xs ${dm ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-200 text-slate-600'}`}>
        <div className="flex justify-between mb-1"><span>Safe</span><span className="text-emerald-500 font-semibold">No issues detected</span></div>
        <div className="flex justify-between mb-1"><span>Flagged</span><span className="text-red-500 font-semibold">Potentially harmful content</span></div>
        <div className="flex justify-between"><span>Total</span><span className="text-blue-500 font-semibold">All events recorded</span></div>
      </div>

      <H2>Show all digital activities</H2>
      <p className={`text-sm leading-relaxed ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        The Digital tab hides routine "safe" browsing rows by default to reduce noise.
        Tap <strong>Show all</strong> at the top of the tab to see the complete list, including every
        page visit recorded during the school day.
      </p>

      <Callout type="tip" title="Alerts are not punishments" dm={dm}>
        When content is flagged, the system gives you a chance to rephrase before a teacher is
        notified. Use that window — it earns you +10 points and keeps your record clean.
      </Callout>
    </div>
  );
}

function GamesSection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="green" dm={dm}>Earn Points</Badge>
      </div>
      <h1 className={`text-2xl font-bold mb-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Games</h1>
      <p className={`text-sm leading-relaxed mb-8 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        The Games tab is where you play digital-citizenship mini-games to earn safety points,
        level up, and climb the leaderboard — all while practising real online-safety skills.
      </p>

      <H2>How points work in games</H2>
      <p className={`text-sm leading-relaxed mb-4 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        Each game awards points based on your score. Points are added to your total immediately and
        contribute to your weekly progress bar on the Home tab. Higher difficulty levels award more
        points — start at the level that feels comfortable.
      </p>

      <H2>Types of games</H2>
      <FeatureGrid dm={dm} features={[
        { icon: <Shield size={16} />, title: 'Phishing Spotter', desc: 'Identify fake emails and scam links before the timer runs out.' },
        { icon: <Lock size={16} />,   title: 'Password Builder', desc: 'Create strong passwords by selecting the right mix of characters.' },
        { icon: <Eye size={16} />,    title: 'Privacy Check',    desc: 'Review social profiles and settings to find privacy risks.' },
        { icon: <Heart size={16} />,  title: 'Kindness Quest',   desc: 'Choose the best response in online conversations to practise positive digital citizenship.' },
      ]} />

      <H2>Leaderboard</H2>
      <p className={`text-sm leading-relaxed ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        Your class leaderboard is updated in real time. Points from games and good online behaviour
        both count toward your rank. If your teacher has enabled private mode, you will only see
        your own rank — not your classmates' scores.
      </p>

      <Callout type="tip" title="Play daily for a streak bonus" dm={dm}>
        Earning at least one point per day keeps your streak alive. A long streak is one of the best
        ways to improve your class rank fast.
      </Callout>
    </div>
  );
}

function SafetySection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="red" dm={dm}>Always Available</Badge>
        <span className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>24/7 crisis support</span>
      </div>
      <h1 className={`text-2xl font-bold mb-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Safety & Support</h1>
      <p className={`text-sm leading-relaxed mb-8 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        The Safety tab puts crisis resources, counsellor contacts, and online-safety advice in
        one place. You can access it at any time — no login required once the app is open.
      </p>

      <H2>Crisis resources</H2>
      <div className="space-y-3 my-4">
        {[
          {
            label: 'Emergency Services', number: '911',
            bg: dm ? 'bg-red-950/40 border-red-800' : 'bg-red-50 border-red-200',
            text: dm ? 'text-red-300' : 'text-red-800',
            desc: 'Immediate danger or life-threatening situation.',
          },
          {
            label: 'Crisis Text Line', number: 'Text HOME to 741741',
            bg: dm ? 'bg-red-950/40 border-red-800' : 'bg-red-50 border-red-200',
            text: dm ? 'text-red-300' : 'text-red-800',
            desc: 'Free 24/7 mental health support via text.',
          },
        ].map(r => (
          <div key={r.label} className={`rounded-xl border p-4 ${r.bg}`}>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-3 h-3 rounded-full bg-red-500 shrink-0" />
              <p className={`font-bold text-sm ${r.text}`}>{r.label}</p>
            </div>
            <p className={`text-xs leading-relaxed mb-2 ${dm ? 'text-slate-400' : 'text-slate-600'}`}>{r.desc}</p>
            <ul className="space-y-1">
              <li className={`text-xs flex items-center gap-2 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
                <span className="w-1 h-1 rounded-full bg-current shrink-0 opacity-60" />
                {r.number}
              </li>
            </ul>
          </div>
        ))}
      </div>

      <H2>Mental health support</H2>
      <FeatureGrid dm={dm} features={[
        { icon: <Heart size={16} />, title: 'School Counsellor',  desc: 'Available Mon–Fri 8 AM – 4 PM. Tap "Schedule Appointment" on the Safety screen.' },
        { icon: <User size={16} />,  title: 'Peer Support Group', desc: 'Connect with other students in a moderated, safe-space group.' },
      ]} />

      <H2>Online safety tips covered</H2>
      <ul className="space-y-2 my-4">
        {[
          { label: 'Password security', desc: 'Use unique, strong passwords — never share them with friends.' },
          { label: 'Privacy settings',  desc: 'Check social media privacy settings and keep profiles private.' },
          { label: 'Stranger safety',   desc: 'Never share personal info (address, phone, school) with strangers online.' },
        ].map(item => (
          <li key={item.label} className={`flex items-start gap-3 text-sm ${dm ? 'text-slate-300' : 'text-slate-700'}`}>
            <ChevronRight size={14} className="text-blue-500 mt-0.5 shrink-0" />
            <span><strong>{item.label}</strong> - {item.desc}</span>
          </li>
        ))}
      </ul>

      <Callout type="warning" title="You are never alone" dm={dm}>
        If something online is making you feel unsafe or upset, tell a trusted adult straight away.
        The school counsellor and the Crisis Text Line are both confidential options.
      </Callout>
    </div>
  );
}

function ProfileSection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  const SL = dm ? StepListDark : StepList;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="slate" dm={dm}>Account</Badge>
      </div>
      <h1 className={`text-2xl font-bold mb-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Profile</h1>
      <p className={`text-sm leading-relaxed mb-8 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        The Profile tab shows your personal information, achievement badges, app permissions,
        and account settings. Changes you make here are saved on your device.
      </p>

      <H2>Profile sections</H2>
      <FeatureGrid dm={dm} features={[
        { icon: <User size={16} />,  title: 'Personal Info',   desc: 'Your email, school name, enrolment date, and student ID.' },
        { icon: <Award size={16} />, title: 'Stats Strip',     desc: 'Live count of your total points, class rank, and badges unlocked.' },
        { icon: <Bell size={16} />,  title: 'App Permissions', desc: 'Enable or manage push notifications and safety monitoring.' },
        { icon: <Lock size={16} />,  title: 'Resources',       desc: 'Help & Support, Contact Us, Privacy Policy, and Log Out.' },
      ]} />

      <H2>Enabling push notifications</H2>
      <SL steps={[
        { title: 'Open the Profile tab',                  desc: 'Tap the person icon in the bottom navigation bar.' },
        { title: 'Find App & Permissions',                desc: 'Scroll to the "App & Permissions" section.' },
        { title: 'Tap Enable next to Push Notifications', desc: 'Your browser will ask you to confirm. Tap Allow.' },
        { title: 'Notifications are now live',            desc: "You'll receive alerts from your teacher and the safety system instantly." },
      ]} />

      <Callout type="info" title="Safety monitoring" dm={dm}>
        The Safety Monitoring toggle in your Profile controls whether the ShieldED Haven extension
        is active on your device. Your teacher can see its status — keep it on to avoid flags.
      </Callout>
    </div>
  );
}

function NotificationsSection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="blue" dm={dm}>Messages</Badge>
        <span className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>Polls every 60 seconds</span>
      </div>
      <h1 className={`text-2xl font-bold mb-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Notifications</h1>
      <p className={`text-sm leading-relaxed mb-8 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        The Notifications page is your in-app inbox. Messages from your teacher, school admin,
        and the safety system all arrive here. Urgent messages are highlighted and delivered as
        push notifications if you've enabled them.
      </p>

      <H2>Urgency levels</H2>
      <div className="space-y-2 my-4">
        {[
          { level: 'Urgent', color: 'bg-red-100 border-red-300 text-red-800',        dot: 'bg-red-500',    note: 'Requires immediate attention. Usually from the safety system.' },
          { level: 'Alert',  color: 'bg-orange-100 border-orange-300 text-orange-800', dot: 'bg-orange-500', note: 'Important but not an emergency. Review within the hour.' },
          { level: 'Info',   color: 'bg-blue-100 border-blue-300 text-blue-800',     dot: 'bg-blue-400',   note: 'General messages from your teacher or school.' },
        ].map(r => (
          <div key={r.level} className={`flex items-center gap-4 p-3 rounded-lg border text-sm ${r.color}`}>
            <div className={`w-2.5 h-2.5 rounded-full ${r.dot} shrink-0`} />
            <span className="font-semibold w-20">{r.level}</span>
            <span className="text-xs">{r.note}</span>
          </div>
        ))}
      </div>

      <H2>Opening a notification</H2>
      <p className={`text-sm leading-relaxed mb-4 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        Tap any row to open the full message. Unread notifications have a coloured dot on the left.
        Opening a notification automatically marks it as read and decreases the badge count on the
        bell icon.
      </p>

      <H2>Mark all read</H2>
      <p className={`text-sm leading-relaxed ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        Tap <strong>Mark all read</strong> in the top-right corner of the Notifications page to
        clear all unread indicators at once.
      </p>

      <Callout type="tip" title="Enable push notifications" dm={dm}>
        Push notifications mean urgent alerts reach you even when the app is in the background.
        Enable them in your Profile → App & Permissions → Push Notifications.
      </Callout>
    </div>
  );
}

function SelfCorrectionSection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  const SL = dm ? StepListDark : StepList;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="green" dm={dm}>+10 pts</Badge>
        <span className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>15-minute window</span>
      </div>
      <h1 className={`text-2xl font-bold mb-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Self-Correction Banner</h1>
      <p className={`text-sm leading-relaxed mb-8 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        When the safety system detects potentially harmful content in something you typed, it gives
        you a chance to rephrase before a teacher is notified. This is the self-correction banner —
        it appears at the top of the screen.
      </p>

      <H2>How it works</H2>
      <SL steps={[
        { title: 'Banner appears',      desc: 'A card slides in at the top of the app showing what was detected and why.' },
        { title: 'Read the suggestion', desc: 'The banner shows a friendlier rephrasing of what you wrote.' },
        { title: 'Tap "Rephrase It"',   desc: 'Submitting the rephrased version earns you +10 points and clears the alert.' },
        { title: 'Breathe 🌿',          desc: 'A short breathing card appears for 5 seconds — a mindful moment before you continue.' },
      ]} />

      <H2>The 15-minute window</H2>
      <p className={`text-sm leading-relaxed mb-4 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        You have 15 minutes to rephrase. A countdown timer in the banner shows how many minutes
        remain. If the window expires without action, the original content is reviewed by your teacher.
      </p>

      <H2>What happens if you dismiss it?</H2>
      <p className={`text-sm leading-relaxed ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        Tapping the × button hides the banner for this session, but the timer keeps running in the
        background. The correction opportunity is still available if you navigate away and return
        before time expires.
      </p>

      <Callout type="warning" title="Rephrasing protects your record" dm={dm}>
        Successfully rephrasing resolves the alert automatically. If the window expires without
        action, the content is escalated to your teacher for review.
      </Callout>
    </div>
  );
}

function GettingStartedSection({ dm }: { dm: boolean }) {
  const H2 = dm ? SectionHeadingDark : SectionHeading;
  const SL = dm ? StepListDark : StepList;
  return (
    <div>
      <div className="flex items-center gap-2 mb-2">
        <Badge variant="green" dm={dm}>Onboarding</Badge>
      </div>
      <h1 className={`text-2xl font-bold mb-2 ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Getting Started</h1>
      <p className={`text-sm leading-relaxed mb-8 ${dm ? 'text-slate-400' : 'text-slate-500'}`}>
        New to ShieldED Haven? Follow these steps to set up your account and start earning points
        in under 5 minutes.
      </p>

      <H2>Initial Setup</H2>
      <SL steps={[
        { title: 'Open the app and log in',           desc: 'Use the credentials provided by your school. You may be asked to set a new password on your first login.' },
        { title: 'Allow notifications when prompted', desc: 'The permission prompt appears on first launch. Tap "Allow & Continue" so urgent alerts reach you.' },
        { title: 'Explore the Home tab',              desc: 'Check your wellness score and current points. If it shows zero, play a game to earn your first points.' },
        { title: 'Play a game',                       desc: 'Tap the Games tab and try Phishing Spotter — it only takes 2 minutes and awards points immediately.' },
        { title: 'Check your Notifications',          desc: 'Tap the bell icon at the top of the screen. Your teacher may have sent a welcome message.' },
      ]} />

      <H2>Daily Workflow</H2>
      <div className="space-y-3 my-4">
        {[
          { time: 'Morning',      action: "Open the Home tab. Check your wellness score and read today's safety tip." },
          { time: 'During class', action: 'Browse only approved sites. The safety system records your activity — good behaviour earns points.' },
          { time: 'After class',  action: 'Play a game in the Games tab to top up your daily points and keep your streak alive.' },
          { time: 'Evening',      action: 'Check Notifications for any messages from your teacher.' },
        ].map(w => (
          <div key={w.time} className={`flex gap-4 text-sm ${dm ? 'text-slate-300' : 'text-slate-700'}`}>
            <span className="font-semibold text-blue-500 w-28 shrink-0">{w.time}</span>
            <span>{w.action}</span>
          </div>
        ))}
      </div>

      <H2>Need Help?</H2>
      <ul className="space-y-2 my-4">
        {[
          'Check this documentation for the relevant section.',
          'Ask your teacher for help with account or activity questions.',
          'Contact your school IT administrator for device issues.',
        ].map((item, i) => (
          <li key={i} className={`flex items-start gap-3 text-sm ${dm ? 'text-slate-300' : 'text-slate-700'}`}>
            <span className={`text-xs font-bold mt-0.5 ${dm ? 'text-blue-400' : 'text-blue-600'}`}>{i + 1}.</span>
            {item}
          </li>
        ))}
      </ul>

      <div className={`rounded-2xl p-6 mt-8 text-center ${dm ? 'bg-gradient-to-br from-emerald-900/50 to-teal-900/50 border border-emerald-700' : 'bg-gradient-to-br from-emerald-500 to-teal-600'}`}>
        <CheckCircle size={32} className="text-white mx-auto mb-3" />
        <h3 className="text-white font-bold text-lg mb-2">You're all set!</h3>
        <p className="text-emerald-100 text-sm leading-relaxed max-w-sm mx-auto">
          Welcome to ShieldED Haven. Make great choices online and watch your points grow. 🌟
        </p>
      </div>
    </div>
  );
}

// ── Section registry ──────────────────────────────────────────────────────────

const SECTIONS = [
  { id: 'overview',        label: 'Overview',         icon: BookOpen,      desc: 'Guide home',              component: OverviewSection       },
  { id: 'home',            label: 'Home',             icon: Home,          desc: 'Dashboard & wellness',    component: HomeSection           },
  { id: 'classroom',       label: 'Classroom',        icon: BookOpen,      desc: 'Activity & alerts log',   component: ClassroomSection      },
  { id: 'games',           label: 'Games',            icon: Gamepad2,      desc: 'Earn points & level up',  component: GamesSection          },
  { id: 'safety',          label: 'Safety & Support', icon: Shield,        desc: 'Crisis & counselling',    component: SafetySection         },
  { id: 'profile',         label: 'Profile',          icon: User,          desc: 'Account & settings',      component: ProfileSection        },
  { id: 'notifications',   label: 'Notifications',    icon: Bell,          desc: 'Messages & alerts',       component: NotificationsSection  },
  { id: 'self-correction', label: 'Self-Correction',  icon: MessageSquare, desc: 'Rephrase & earn +10 pts', component: SelfCorrectionSection },
  { id: 'getting-started', label: 'Getting Started',  icon: HelpCircle,    desc: 'Quick-start guide',       component: GettingStartedSection },
];

// ── Page component ────────────────────────────────────────────────────────────

const Documentation: React.FC<DocumentationProps> = ({ darkMode: dm = false }) => {
  const [activeSection, setActiveSection] = useState('overview');

  const section = SECTIONS.find(s => s.id === activeSection) ?? SECTIONS[0];
  const SectionComponent = section.component;
  const currentIdx = SECTIONS.findIndex(s => s.id === activeSection);

  return (
    <div className={`h-full flex overflow-hidden rounded-xl border ${
      dm ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200'
    }`}>

      {/* ── Left sidebar ── */}
      <aside className={`w-64 flex flex-col shrink-0 border-r ${
        dm ? 'bg-slate-900 border-slate-800' : 'bg-slate-50 border-slate-200'
      }`}>
        {/* Header */}
        <div className={`px-4 py-4 border-b ${dm ? 'border-slate-800' : 'border-slate-200'}`}>
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
              <BookOpen size={15} className="text-white" />
            </div>
            <div>
              <p className={`font-bold text-sm ${dm ? 'text-slate-100' : 'text-slate-900'}`}>Documentation</p>
              <p className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-500'}`}>Student guide</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 overflow-y-auto px-2 py-2 space-y-0.5">
          {SECTIONS.map(s => {
            const active = activeSection === s.id;
            const Icon = s.icon;
            return (
              <button
                key={s.id}
                onClick={() => setActiveSection(s.id)}
                style={{
                  appearance: 'none', border: 'none', cursor: 'pointer',
                  width: '100%', display: 'flex', alignItems: 'center', gap: '0.625rem',
                  padding: '0.4rem 0.625rem', borderRadius: '0.5rem',
                  backgroundColor: active ? '#2563eb' : 'transparent',
                  color: active ? '#ffffff' : (dm ? '#94a3b8' : '#475569'),
                  transition: 'background-color 0.15s, color 0.15s',
                  textAlign: 'left',
                }}
                onMouseEnter={e => { if (!active) { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = dm ? '#1e293b' : '#e2e8f0'; b.style.color = dm ? '#e2e8f0' : '#1e293b'; }}}
                onMouseLeave={e => { if (!active) { const b = e.currentTarget as HTMLButtonElement; b.style.backgroundColor = 'transparent'; b.style.color = dm ? '#94a3b8' : '#475569'; }}}
              >
                <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 ${active ? 'bg-white/20' : (dm ? 'bg-slate-800' : 'bg-white border border-slate-200')}`}>
                  <Icon size={13} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-medium leading-none truncate">{s.label}</p>
                  <p className={`text-xs mt-0.5 truncate opacity-70 ${active ? '' : ''}`}>{s.desc}</p>
                </div>
                {active && <ChevronRight size={12} className="ml-auto text-blue-200 shrink-0" />}
              </button>
            );
          })}
        </nav>

        {/* Footer */}
        <div className={`px-4 py-3 border-t ${dm ? 'border-slate-800' : 'border-slate-200'}`}>
          <p className={`text-xs ${dm ? 'text-slate-600' : 'text-slate-400'}`}>
            ShieldED Haven &mdash; April 2026
          </p>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div className={`flex-1 flex flex-col min-w-0 ${dm ? 'bg-slate-950' : 'bg-white'}`}>

        {/* Breadcrumb bar */}
        <div className={`flex items-center gap-2 px-6 py-3 border-b shrink-0 ${
          dm ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'
        }`}>
          <section.icon size={14} className={dm ? 'text-blue-400' : 'text-blue-600'} />
          <span className={`font-semibold text-xs ${dm ? 'text-slate-100' : 'text-slate-800'}`}>{section.label}</span>
          <ChevronRight size={11} className={dm ? 'text-slate-600' : 'text-slate-300'} />
          <span className={`text-xs ${dm ? 'text-slate-500' : 'text-slate-400'}`}>{section.desc}</span>
        </div>

        {/* Scrollable body */}
        <div className="flex-1 overflow-y-auto">
          <div className="max-w-2xl mx-auto px-8 py-8">
            <SectionComponent dm={dm} />

            {/* Help box */}
            <div className={`mt-12 rounded-2xl p-6 border ${
              dm ? 'bg-slate-900 border-slate-700' : 'bg-blue-50 border-blue-200'
            }`}>
              <div className="flex items-start gap-4">
                <div className="w-9 h-9 bg-blue-600 rounded-xl flex items-center justify-center shrink-0">
                  <HelpCircle size={18} className="text-white" />
                </div>
                <div>
                  <h3 className={`font-bold text-sm mb-1 ${dm ? 'text-slate-200' : 'text-blue-900'}`}>Need more help?</h3>
                  <p className={`text-xs leading-relaxed mb-3 ${dm ? 'text-slate-400' : 'text-blue-700'}`}>
                    Ask your teacher, visit the Safety tab for crisis support, or contact school IT for account issues.
                  </p>
                  <div className="flex gap-2">
                    <button
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
                      style={{ border: 'none', cursor: 'pointer' }}
                    >
                      Contact Support
                    </button>
                    <button
                      className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-xs font-semibold hover:bg-blue-700 transition-colors"
                      style={{ border: 'none', cursor: 'pointer' }}
                    >
                      Browse All Sections
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Prev / Next navigation */}
            <div className="flex items-center justify-between mt-8 pb-4">
              {currentIdx > 0 ? (
                <button
                  onClick={() => setActiveSection(SECTIONS[currentIdx - 1].id)}
                  className={`flex items-center gap-2 text-xs font-medium transition-colors ${
                    dm ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'
                  }`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  <ArrowRight size={12} className="rotate-180" />
                  {SECTIONS[currentIdx - 1].label}
                </button>
              ) : <div />}
              {currentIdx < SECTIONS.length - 1 ? (
                <button
                  onClick={() => setActiveSection(SECTIONS[currentIdx + 1].id)}
                  className={`flex items-center gap-2 text-xs font-medium transition-colors ${
                    dm ? 'text-slate-400 hover:text-slate-200' : 'text-slate-400 hover:text-slate-700'
                  }`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                >
                  {SECTIONS[currentIdx + 1].label}
                  <ArrowRight size={12} />
                </button>
              ) : <div />}
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;